<template>
  <main class="multicolumn-view">
    <h1 class="view-full-span">{{ $t('section-titles.profile') }}</h1>

    <div class="bowler-profile-data-section">
      <h2>{{ $t('section-titles.general-information') }}</h2>

      <ValueWithLabel
        :value="bowlerFullName"
        :label="$t('field-labels.first-and-last-names')"
      />

      <ValueWithLabel
        type="date"
        :value="bowler.birthDate"
        :label="$t('field-labels.date-of-birth')"
      />

      <ValueWithLabel
        type="gender"
        :value="bowler.gender"
        :label="$t('field-labels.gender')"
      />
    </div>

    <div class="bowler-profile-data-section">
      <div class="data-section-heading">
        <h2>{{ $t('section-titles.contact-information') }}</h2>
        <FormButton v-if="!isEditMode" is-small @click="editBowlerInfo">
          {{ $t('general.edit') }}
        </FormButton>
      </div>

      <BowlerEditableInfoForm
        v-if="isEditMode"
        :initial-data="bowlerContactDetails"
        @dismiss="isEditMode = false"
        @success="isUpdateSuccessful = true"
      />

      <template v-else>
        <NotificationBanner
          v-if="isUpdateSuccessful"
          is-success
          message="Your details have been updated successfully"
        />

        <ValueWithLabel
          :value="bowler.email"
          :label="$t('field-labels.email')"
        />

        <ValueWithLabel
          :value="bowler.phoneNumber"
          :label="$t('field-labels.phone')"
        />

        <ValueWithLabel
          :value="bowler.streetAddress"
          :label="$t('field-labels.street-address')"
        />

        <ValueWithLabel
          :value="bowler.postalCode"
          :label="$t('field-labels.postal-code')"
        />

        <ValueWithLabel :value="bowler.city" :label="$t('field-labels.city')" />

        <ValueWithLabel
          :value="formatBoolean(bowler.isMarketingPermitted)"
          :label="$t('field-labels.is-marketing-permitted')"
        />
      </template>
    </div>

    <div class="bowler-profile-data-section">
      <h2>{{ $t('section-titles.membership') }}</h2>

      <ValueWithLabel
        :value="bowler.activeMembership?.clubName"
        :label="$t('field-labels.club')"
      />

      <ValueWithLabel
        :value="bowler.activeMembership?.associationName"
        :label="$t('field-labels.association')"
      />

      <ValueWithLabel
        type="date"
        :value="bowler.firstRegistrationDate"
        :label="$t('field-labels.registration-date')"
      />

      <ValueWithLabel
        :value="bowler.memberNumber"
        :label="$t('field-labels.code')"
      />

      <ValueWithLabel
        :value="formatValue(bowler.currentClass)"
        :label="$t('field-labels.class')"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import FormButton from '@/components/common/FormButton.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import ValueWithLabel from '@/components/common/ValueWithLabel.vue'
import BowlerEditableInfoForm from '@/components/forms/BowlerEditableInfoForm.vue'
import { useBowlerStore } from '@/store/bowler-app/bowler'
import { formatBoolean, formatValue } from '@/utils/format'

const router = useRouter()
const bowler = useBowlerStore()

interface ContactDetails {
  email: string
  phoneNumber: string
  streetAddress: string
  postalCode: string
  city: string
  country: string
  isMarketingPermitted: boolean
}

const isEditMode = ref<boolean>(false)
const isUpdateSuccessful = ref<boolean>(false)

/* actions */

const editBowlerInfo = () => {
  isUpdateSuccessful.value = false
  isEditMode.value = true
}

/* hooks */

onBeforeMount(async (): Promise<void> => {
  if (isNil(bowler.id)) {
    await router.push({ path: '/' })
  }
})

/* computed */

const bowlerContactDetails = computed<ContactDetails>(
  (): ContactDetails => bowler as ContactDetails,
)

const bowlerFullName = computed<string>((): string => {
  const firstName = bowler.firstName ?? null
  const lastName = bowler.lastName ?? null

  if (!isNil(firstName) && !isNil(lastName)) {
    return `${firstName} ${lastName}`
  }

  return formatValue(firstName ?? lastName)
})
</script>

<style lang="scss" scoped>
.bowler-profile-data-section {
  display: flex;
  flex-flow: column nowrap;
  gap: 1em;

  .data-section-heading {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 1.5em;
  }
}
</style>
